import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import { pollFlyerStatus as pollFlyerStatusUntilTerminal } from "../../features/admin/pollFlyerStatus";
import type { GalleryItem } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";
import RefreshButton from "../../components/admin/RefreshButton";

const COLLECTIONS = [
  "deals",
  "deal_stays",
  "deal_packages",
  "deal_flights",
  "deal_cruises",
  "deal_transfers",
  "deal_experiences",
  "deal_services",
  "deal_media",
];

const STATUS_BADGE: Record<GalleryItem["status"], string> = {
  pending: "bg-amber-500/10 text-amber-400",
  approved: "bg-green-500/10 text-green-400",
  rejected: "bg-red-500/10 text-red-400",
};

type FieldKey = "gallery" | "preview_pages_urls";

const FIELDS: { key: FieldKey; label: string }[] = [
  { key: "gallery", label: "Gallery" },
  { key: "preview_pages_urls", label: "Preview Pages" },
];

const MediaLightbox: React.FC<{ url: string; isVideo?: boolean; alt?: string; onClose: () => void }> = ({ url, isVideo, alt, onClose }) => {
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-800/80 hover:bg-gray-700 text-white text-lg"
      >
        ×
      </button>
      <div className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video src={url} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg" />
        ) : (
          <img src={url} alt={alt || "media"} className="max-w-full max-h-[85vh] rounded-lg object-contain" />
        )}
      </div>
    </div>,
    document.body,
  );
};

const GalleryThumb: React.FC<{
  item: GalleryItem;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onDelete: () => void;
  busy: boolean;
  onView: () => void;
  /** Deal's own seller id + resolved name — used to substitute a readable
   *  name for `uploaded_by` when it's the deal's own seller (the vast
   *  majority of real, non-backfill uploads). */
  dealerId?: string;
  dealerName?: string;
  downloadName: string;
}> = ({ item, onApprove, onReject, onDelete, busy, onView, dealerId, dealerName, downloadName }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const isVideo = item.media_type === "video";
  const isMissing = item.missing || !item.display_url;

  return (
    <div className={`bg-gray-900 border rounded-xl overflow-hidden flex flex-col ${isMissing ? "border-red-500/50" : "border-gray-800"}`}>
      <button
        type="button"
        onClick={isMissing ? undefined : onView}
        disabled={isMissing}
        className={`aspect-video bg-gray-950 flex items-center justify-center overflow-hidden ${isMissing ? "cursor-default" : "cursor-zoom-in"}`}
      >
        {isMissing ? (
          <span className="flex flex-col items-center justify-center gap-1 text-red-400">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
            <span className="text-[10px] leading-tight">Missing from storage</span>
          </span>
        ) : isVideo ? (
          <video src={item.display_url!} className="w-full h-full object-cover pointer-events-none" />
        ) : (
          <img src={item.display_url!} alt={item.media_type} className="w-full h-full object-cover" />
        )}
      </button>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[item.status]}`}>
            {item.status}
          </span>
          <span className="text-[10px] text-gray-500 font-mono uppercase">{item.media_type}</span>
        </div>
        {isMissing ? (
          <p className="text-[11px] text-red-400">S3 object not found — the file itself is gone. Delete this entry to clean it up.</p>
        ) : (
          <DownloadButton
            url={item.display_url!}
            filename={downloadName}
            className="self-start px-2 py-0.5 text-[10px] font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors border border-indigo-500/20"
          />
        )}
        <div className="text-[11px] text-gray-500 space-y-0.5">
          {item.uploaded_by && (
            <p>Uploaded by: <span className="text-gray-400">{
              item.uploaded_by === "system-backfill"
                ? "System (migrated)"
                : dealerName && dealerId && item.uploaded_by === dealerId
                  ? dealerName
                  : item.uploaded_by
            }</span></p>
          )}
          {item.uploaded_at && <p>Uploaded: {formatIST(item.uploaded_at)}</p>}
          {item.status === "rejected" && item.rejection_reason && (
            <p className="text-red-400 italic">"{item.rejection_reason}"</p>
          )}
        </div>

        {rejecting ? (
          <div className="space-y-2 mt-1">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rejection…"
              className="w-full text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { if (reason.trim()) { onReject(reason.trim()); setRejecting(false); setReason(""); } }}
                disabled={busy || !reason.trim()}
                className="flex-1 px-2 py-1 text-xs font-medium bg-red-600 hover:bg-red-500 text-white rounded-md disabled:opacity-50 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => { setRejecting(false); setReason(""); }}
                disabled={busy}
                className="px-2 py-1 text-xs text-gray-400 hover:bg-gray-800 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 mt-1">
            {(item.status === "pending" || item.status === "rejected") && (
              <button
                onClick={onApprove}
                disabled={busy}
                className="flex-1 px-2 py-1 text-xs font-medium text-emerald-400 hover:bg-emerald-500/10 rounded-md transition-colors disabled:opacity-50"
              >
                {item.status === "rejected" ? "Reapprove" : "Approve"}
              </button>
            )}
            {(item.status === "pending" || item.status === "approved") && (
              <button
                onClick={() => setRejecting(true)}
                disabled={busy}
                className="flex-1 px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
              >
                Reject
              </button>
            )}
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              title="Permanently delete from S3 and the deal"
              className="px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-500/10 hover:text-red-400 rounded-md transition-colors disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>
      {confirmingDelete && (
        <ConfirmDeleteModal
          title="Delete gallery item?"
          description={`This permanently removes this ${item.media_type} — it can't be undone.`}
          locations={["S3 storage (the file itself)", "This deal's document (deals collection)", "This deal's typed collection (e.g. deal_stays), if mirrored there"]}
          busy={busy}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => { onDelete(); setConfirmingDelete(false); }}
        />
      )}
    </div>
  );
};

/** Small provenance tag: who/what last set this asset — seller upload, admin
 * replace, or (for the AI flyer) the auto-generation pipeline that ran on
 * deal upload vs. the one an admin replace re-triggered. */
/** Triggers a save-as with a deal-title-based filename. A plain `<a download>`
 * can't rename or force-download a cross-origin (S3) URL — the attribute is
 * ignored cross-origin, so the browser just navigates to it. Instead we ask
 * the backend for a presigned URL with Content-Disposition: attachment
 * already baked in; navigating to that (not fetching it) isn't subject to
 * CORS, and S3 sends the attachment header itself, so it downloads directly. */
async function downloadAsset(url: string, filename: string) {
  try {
    const downloadUrl = await adminService.getMediaDownloadUrl(url, filename);
    const a = document.createElement("a");
    a.href = downloadUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    window.open(url, "_blank");
  }
}

function extFromUrl(url: string | null | undefined): string {
  if (!url) return "png";
  const match = /\.([a-zA-Z0-9]{2,5})(?:\?|$)/.exec(url.split("/").pop() || "");
  return match ? match[1] : "png";
}

function slugifyTitle(title: string | undefined): string {
  const base = (title || "deal").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return base || "deal";
}

const DownloadButton: React.FC<{ url: string; filename: string; className?: string }> = ({ url, filename, className }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); downloadAsset(url, filename); }}
    title={`Download as ${filename}`}
    className={className || "px-2 py-1 text-[11px] font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors"}
  >
    Download
  </button>
);

const SourceBadge: React.FC<{ source: string | null; kind: "image" | "flyer" }> = ({ source, kind }) => {
  if (!source) {
    return kind === "flyer" ? null : (
      <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-700/60 text-gray-400">seller upload</span>
    );
  }
  if (source === "admin_upload" && kind === "flyer") {
    return <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-500/15 text-red-400">admin override (locked)</span>;
  }
  const isAdmin = source === "admin_replace";
  const label = isAdmin
    ? (kind === "flyer" ? "admin-triggered" : "admin replaced")
    : "seller upload";
  const cls = isAdmin ? "bg-amber-500/15 text-amber-400" : "bg-gray-700/60 text-gray-400";
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${cls}`}>{label}</span>;
};

const ConfirmDeleteModal: React.FC<{
  title: string;
  description: string;
  locations: string[];
  busy: boolean;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, description, locations, busy, confirmLabel, onCancel, onConfirm }) => {
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={busy ? undefined : onCancel}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-xl p-5 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-white font-semibold mb-1">{title}</h3>
        <p className="text-sm text-gray-400 mb-3">{description}</p>
        <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">This will permanently delete from:</p>
        <ul className="text-sm text-red-300 list-disc pl-5 mb-4 space-y-0.5">
          {locations.map((l) => <li key={l}>{l}</li>)}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {busy ? "Deleting…" : confirmLabel || "Delete permanently"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ConfirmFlyerReplaceModal: React.FC<{
  file: File;
  currentAiFlyerUrl: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ file, currentAiFlyerUrl, busy, onCancel, onConfirm }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={busy ? undefined : onCancel}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-xl p-5 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-white font-semibold mb-1">Replace original?</h3>
        <p className="text-sm text-gray-400 mb-4">
          {file.type.startsWith("video/") ? (
            <>This will replace the original video clip. Videos never get an AI flyer.</>
          ) : (
            <>
              This will re-run AI flyer generation from the new file and{" "}
              <span className="text-red-400 font-medium">permanently replace the current AI-generated flyer</span>{" "}
              below.
            </>
          )}{" "}
          The old original (and old AI flyer, where applicable) is deleted from storage — this can't be undone.
        </p>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Current AI Flyer</p>
            <div className="aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
              {currentAiFlyerUrl ? (
                <img src={currentAiFlyerUrl} alt="Current AI flyer" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-600">No image</span>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">New Original</p>
            <div className="aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
              {previewUrl && file.type.startsWith("image/") ? (
                <img src={previewUrl} alt="New original" className="w-full h-full object-cover" />
              ) : previewUrl && file.type.startsWith("video/") ? (
                <video src={previewUrl} className="w-full h-full object-cover" muted controls />
              ) : (
                <span className="text-xs text-gray-600 px-2 text-center">{file.name}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {busy ? "Replacing…" : file.type.startsWith("video/") ? "Replace video" : "Replace & regenerate flyer"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ConfirmFlyerOverrideModal: React.FC<{
  file: File;
  currentAiFlyerUrl: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ file, currentAiFlyerUrl, busy, onCancel, onConfirm }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={busy ? undefined : onCancel}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-xl p-5 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-white font-semibold mb-1">Override AI flyer?</h3>
        <p className="text-sm text-gray-400 mb-4">
          This sets this image as the deal's AI-generated flyer and{" "}
          <span className="text-red-400 font-medium">permanently locks it</span> — auto-generation
          will never overwrite it again, even if the original asset is later replaced. The old
          flyer's S3 object is deleted. Use "Delete AI flyer" afterward to remove the override and
          let auto-generation resume.
        </p>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Current AI Flyer</p>
            <div className="aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
              {currentAiFlyerUrl ? (
                <img src={currentAiFlyerUrl} alt="Current AI flyer" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-600">No image</span>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">New Flyer (locked)</p>
            <div className="aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
              {previewUrl ? (
                <img src={previewUrl} alt="New flyer" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-gray-600 px-2 text-center">{file.name}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {busy ? "Uploading…" : "Override & lock flyer"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const AdminGalleryReviewPage: React.FC = () => {
  const { showError } = useAdminToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collection, setCollection] = useState(searchParams.get("collection") || "deals");
  const [dealId, setDealId] = useState(searchParams.get("deal_id") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [items, setItems] = useState<Record<FieldKey, GalleryItem[]>>({ gallery: [], preview_pages_urls: [] });
  const [loaded, setLoaded] = useState(false);
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<{ url: string; alt: string } | null>(null);
  const [dealSummary, setDealSummary] = useState<{ title?: string; dealer_name?: string; dealer_id?: string } | null>(null);
  // Every "original" field a deal can carry, shown and deletable independently —
  // a deal can legitimately have more than one set at once (e.g. an image left
  // over from before a video replace), so these are never collapsed into a
  // single priority pick the way the old originalUrl/originalKind pair did.
  type OriginalField = "image" | "video" | "pdf" | "document";
  const [originals, setOriginals] = useState<Record<OriginalField, string | null>>({
    image: null, video: null, pdf: null, document: null,
  });
  const [originalSource, setOriginalSource] = useState<string | null>(null);
  const [aiFlyerUrl, setAiFlyerUrl] = useState<string | null>(null);
  const [aiFlyerSource, setAiFlyerSource] = useState<string | null>(null);
  const [aiFlyerLocked, setAiFlyerLocked] = useState(false);
  const [flyerBusy, setFlyerBusy] = useState(false);
  const [flyerError, setFlyerError] = useState<string | null>(null);
  useEffect(() => { if (flyerError) showError(flyerError); }, [flyerError, showError]);
  const [pendingFlyerFile, setPendingFlyerFile] = useState<File | null>(null);
  const [pendingFlyerOverrideFile, setPendingFlyerOverrideFile] = useState<File | null>(null);
  const [flyerOverrideBusy, setFlyerOverrideBusy] = useState(false);
  const [confirmingDeleteOriginal, setConfirmingDeleteOriginal] = useState<OriginalField | null>(null);
  const [confirmingDeleteFlyer, setConfirmingDeleteFlyer] = useState(false);
  const [flyerDeleteBusy, setFlyerDeleteBusy] = useState(false);

  const load = useCallback(async (col: string, id: string) => {
    if (!col || !id.trim()) return;
    setLoading(true);
    setError(null);
    setDealSummary(null);
    setFlyerError(null);
    try {
      const [gallery, previewPages, dealDetail] = await Promise.all([
        adminService.getGalleryReviewItems(col, id.trim(), "gallery"),
        adminService.getGalleryReviewItems(col, id.trim(), "preview_pages_urls"),
        adminService.getDeal(id.trim()).catch(() => null),
      ]);
      setItems({ gallery, preview_pages_urls: previewPages });
      if (dealDetail) {
        setDealSummary({
          title: dealDetail.title,
          dealer_name: dealDetail.dealer_name || dealDetail.seller_name,
          dealer_id: dealDetail.dealer_id || dealDetail.seller_id,
        });
        setOriginals({
          image: dealDetail.media?.image || dealDetail.image || null,
          video: dealDetail.media?.video_url || dealDetail.video_url || null,
          pdf: dealDetail.media?.pdf_url || dealDetail.pdf_url || null,
          document: dealDetail.media?.document_url || dealDetail.document_url || null,
        });
        setOriginalSource(dealDetail.image_source || "seller_upload");
        setAiFlyerUrl(dealDetail.ai_generated_flyer || dealDetail.media?.image_clean || dealDetail.image_clean || null);
        setAiFlyerSource(dealDetail.flyer_source || null);
        setAiFlyerLocked(!!dealDetail.flyer_admin_locked);
      }
      setLoaded(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load gallery items. Check the collection/deal ID.");
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // File picked → hold it and ask for confirmation before actually uploading,
  // since this permanently replaces the current AI flyer (old S3 object is
  // deleted, not just superseded).
  const handleFlyerFilePicked = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file || !dealId.trim()) return;
    setFlyerError(null);
    setPendingFlyerFile(file);
  };

  // Polls the deal doc for flyer_auto_status until the background
  // _generate_and_attach_flyer job (fired by the replace endpoint) reaches a
  // terminal state, so the admin sees a live "Generating…" indicator instead
  // of the button just going back to normal while nothing has actually
  // finished yet. Shared with AdminDealsPage via pollFlyerStatus util.
  const pollFlyerStatus = useCallback(async (id: string) => {
    await pollFlyerStatusUntilTerminal(id, {
      collection,
      onTerminal: (deal, status) => {
        setAiFlyerUrl(deal.ai_generated_flyer || deal.media?.image_clean || deal.image_clean || null);
        setAiFlyerSource(deal.flyer_source || null);
        if (status === "failed") setFlyerError("AI flyer generation failed — check backend logs (flyer_auto).");
        setFlyerBusy(false);
      },
      // Deadline hit with no terminal status — the job may well still be
      // running server-side (flyer_auto_status="running"), so don't claim
      // failure; just tell the admin to check back rather than alarming them.
      onTimeout: () => {
        setFlyerBusy(false);
        setFlyerError((prev) => prev || "AI flyer generation is still running in the background — reload in a minute to check.");
      },
    });
  }, [collection]);

  const confirmFlyerReplace = async () => {
    const file = pendingFlyerFile;
    if (!file) return;
    setFlyerBusy(true);
    setFlyerError(null);
    try {
      const result = await adminService.replaceFlyerImage(collection, dealId.trim(), file);
      // The replace endpoint re-triggers AI flyer regeneration in the background
      // (see gallery_review_routes.py) and deliberately leaves image_clean/
      // ai_generated_flyer alone until that job lands — so the "AI Generated"
      // card here keeps showing the previous flyer rather than going blank.
      // Videos never get a flyer, so aiFlyerUrl is left as-is for that kind.
      // The backend clears every OTHER original field when it sets the new one
      // (replace_flyer_image's `updates` dict), so mirror that here too.
      const newField: OriginalField = result.kind === "video" ? "video" : result.kind === "pdf" ? "pdf" : result.kind === "docx" ? "document" : "image";
      setOriginals({ image: null, video: null, pdf: null, document: null, [newField]: result.display_url });
      setOriginalSource("admin_replace");
      if (result.kind !== "video") {
        setAiFlyerUrl(null);
        pollFlyerStatus(dealId.trim());
      }
      setPendingFlyerFile(null);
      if (result.kind === "video") setFlyerBusy(false);
    } catch (err: any) {
      setFlyerError(err?.response?.data?.detail || "Failed to replace flyer image.");
      setFlyerBusy(false);
    }
  };

  const handleDeleteOriginal = async (field: OriginalField) => {
    setFlyerBusy(true);
    setFlyerError(null);
    try {
      await adminService.deleteOriginalAsset(collection, dealId.trim(), field);
      setOriginals((prev) => ({ ...prev, [field]: null }));
      setConfirmingDeleteOriginal(null);
    } catch (err: any) {
      setFlyerError(err?.response?.data?.detail || `Failed to delete ${field}.`);
    } finally {
      setFlyerBusy(false);
    }
  };

  const handleDeleteAiFlyer = async () => {
    setFlyerDeleteBusy(true);
    setFlyerError(null);
    try {
      await adminService.deleteAiFlyer(collection, dealId.trim());
      setAiFlyerUrl(null);
      setAiFlyerSource(null);
      setAiFlyerLocked(false);
      setConfirmingDeleteFlyer(false);
    } catch (err: any) {
      setFlyerError(err?.response?.data?.detail || "Failed to delete AI flyer.");
    } finally {
      setFlyerDeleteBusy(false);
    }
  };

  // Same picked-file → confirm-before-upload flow as handleFlyerFilePicked,
  // since this permanently locks the flyer against auto-regeneration.
  const handleFlyerOverrideFilePicked = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file || !dealId.trim()) return;
    setFlyerError(null);
    setPendingFlyerOverrideFile(file);
  };

  const confirmFlyerOverride = async () => {
    const file = pendingFlyerOverrideFile;
    if (!file) return;
    setFlyerOverrideBusy(true);
    setFlyerError(null);
    try {
      const result = await adminService.uploadAiFlyer(collection, dealId.trim(), file);
      setAiFlyerUrl(result.display_url);
      setAiFlyerSource("admin_upload");
      setAiFlyerLocked(true);
      setPendingFlyerOverrideFile(null);
    } catch (err: any) {
      setFlyerError(err?.response?.data?.detail || "Failed to override AI flyer.");
    } finally {
      setFlyerOverrideBusy(false);
    }
  };

  // Auto-load if the page was reached via deep link with both params present.
  useEffect(() => {
    const col = searchParams.get("collection");
    const id = searchParams.get("deal_id");
    if (col && id) load(col, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoad = () => {
    setSearchParams({ collection, deal_id: dealId.trim() });
    load(collection, dealId);
  };

  const refetchField = useCallback(async (field: FieldKey) => {
    const fresh = await adminService.getGalleryReviewItems(collection, dealId.trim(), field);
    setItems((prev) => ({ ...prev, [field]: fresh }));
  }, [collection, dealId]);

  const handleApprove = async (field: FieldKey, item: GalleryItem) => {
    setBusyKeys((prev) => new Set(prev).add(item.key));
    try {
      if (item.status === "rejected") {
        await adminService.reapproveGalleryItem(collection, dealId.trim(), field, item.key);
      } else {
        await adminService.approveGalleryItem(collection, dealId.trim(), field, item.key);
      }
      await refetchField(field);
    } catch (err) {
      console.error(err);
    } finally {
      setBusyKeys((prev) => { const next = new Set(prev); next.delete(item.key); return next; });
    }
  };

  const handleReject = async (field: FieldKey, item: GalleryItem, reason: string) => {
    setBusyKeys((prev) => new Set(prev).add(item.key));
    try {
      await adminService.rejectGalleryItem(collection, dealId.trim(), field, item.key, reason);
      await refetchField(field);
    } catch (err) {
      console.error(err);
    } finally {
      setBusyKeys((prev) => { const next = new Set(prev); next.delete(item.key); return next; });
    }
  };

  const handleDeleteGalleryItem = async (field: FieldKey, item: GalleryItem) => {
    setBusyKeys((prev) => new Set(prev).add(item.key));
    try {
      await adminService.deleteGalleryItem(collection, dealId.trim(), field, item.key);
      await refetchField(field);
    } catch (err) {
      console.error(err);
    } finally {
      setBusyKeys((prev) => { const next = new Set(prev); next.delete(item.key); return next; });
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Gallery Media Review"
        subtitle="Approve or reject seller-uploaded gallery images/videos and preview pages"
        actions={<RefreshButton onRefresh={() => load(collection, dealId)} />}
      />

      {loaded && dealSummary && (dealSummary.title || dealSummary.dealer_name) && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-200 text-sm">
          Reviewing: <span className="font-semibold">{dealSummary.title || "Untitled deal"}</span>
          {dealSummary.dealer_name && <> — seller: <span className="font-semibold">{dealSummary.dealer_name}</span></>}
        </div>
      )}

      {/* Lookup */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Collection</label>
          <select
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            className="text-sm px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {COLLECTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Deal ID</label>
          <input
            type="text"
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            placeholder="Paste deal ID…"
            className="w-full text-sm px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <button
          onClick={handleLoad}
          disabled={loading || !dealId.trim()}
          className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 transition-colors"
        >
          {loading ? "Loading…" : "Load"}
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading gallery items…</p>}

      {!loading && loaded && (
        <div className="space-y-8">
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Original Asset(s) vs AI-Generated Flyer</h2>
            <p className="text-xs text-gray-500 mb-3">
              A deal can carry more than one of these at once (e.g. a leftover image field
              from before it was replaced with a video) — each is shown and deletable on its own.
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-wrap items-center gap-4">
              {([
                { field: "image" as OriginalField, label: "Original (image)" },
                { field: "video" as OriginalField, label: "Original (video)" },
                { field: "pdf" as OriginalField, label: "Original (PDF)" },
                { field: "document" as OriginalField, label: "Original (document)" },
              ]).map(({ field, label }) => {
                const url = originals[field];
                if (!url) return null;
                return (
                  <div key={field} className="shrink-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
                      <SourceBadge source={originalSource} kind="image" />
                    </div>
                    <div className="w-40 aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
                      {field === "video" ? (
                        <video src={url} className="w-full h-full object-cover" controls muted />
                      ) : field === "pdf" || field === "document" ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex flex-col items-center gap-1 text-xs text-indigo-300 hover:underline"
                        >
                          Open document
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setLightboxUrl({ url, alt: label })}
                          className="w-full h-full cursor-zoom-in"
                        >
                          <img src={url} alt={label} className="w-full h-full object-cover" />
                        </button>
                      )}
                    </div>
                    <DownloadButton
                      url={url}
                      filename={`${slugifyTitle(dealSummary?.title)}-${field}.${extFromUrl(url)}`}
                      className="mt-1 w-full text-center px-2 py-1 text-[11px] font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors border border-indigo-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteOriginal(field)}
                      disabled={flyerBusy}
                      className="mt-1 w-full text-center px-2 py-1 text-[11px] font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors border border-red-500/20 disabled:opacity-50"
                    >
                      Delete {field}
                    </button>
                  </div>
                );
              })}
              <div className="shrink-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">AI Generated</p>
                  <SourceBadge source={aiFlyerSource} kind="flyer" />
                </div>
                <div className="w-40 aspect-video bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
                  {aiFlyerUrl ? (
                    <button
                      type="button"
                      onClick={() => setLightboxUrl({ url: aiFlyerUrl, alt: "AI generated flyer" })}
                      className="w-full h-full cursor-zoom-in"
                    >
                      <img src={aiFlyerUrl} alt="AI generated flyer" className="w-full h-full object-cover" />
                    </button>
                  ) : flyerBusy ? (
                    <span className="flex flex-col items-center gap-1.5 text-xs text-indigo-300">
                      <span className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                      Generating…
                    </span>
                  ) : (
                    <span className="text-xs text-gray-600">No image</span>
                  )}
                </div>
                {aiFlyerUrl && (
                  <>
                    <DownloadButton
                      url={aiFlyerUrl}
                      filename={`${slugifyTitle(dealSummary?.title)}-ai-flyer.${extFromUrl(aiFlyerUrl)}`}
                      className="mt-1 w-full text-center px-2 py-1 text-[11px] font-medium text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors border border-indigo-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setConfirmingDeleteFlyer(true)}
                      disabled={flyerDeleteBusy}
                      className="mt-1 w-full text-center px-2 py-1 text-[11px] font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors border border-red-500/20 disabled:opacity-50"
                    >
                      Delete AI flyer
                    </button>
                  </>
                )}
                <label className="mt-1 inline-flex w-full items-center justify-center px-2 py-1 text-[11px] font-medium bg-amber-600 hover:bg-amber-500 text-white rounded-md cursor-pointer transition-colors disabled:opacity-50">
                  {flyerOverrideBusy ? "Uploading…" : aiFlyerLocked ? "Replace override" : "Override flyer"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={flyerOverrideBusy}
                    onChange={(e) => { handleFlyerOverrideFilePicked(e.target.files); e.target.value = ""; }}
                  />
                </label>
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-gray-500 mb-2">
                  Replacing the original with an image or PDF/DOCX re-runs AI flyer synthesis
                  against it and permanently overwrites the current AI-generated flyer above.
                  Replacing with a video just swaps the clip — videos never get a flyer. The
                  old original (and old AI flyer, where applicable) is deleted from storage.
                </p>
                <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg cursor-pointer transition-colors disabled:opacity-50">
                  {flyerBusy ? "Uploading…" : "Replace original"}
                  <input
                    type="file"
                    accept="image/*,video/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    disabled={flyerBusy}
                    onChange={(e) => { handleFlyerFilePicked(e.target.files); e.target.value = ""; }}
                  />
                </label>
                {flyerError && <p className="text-xs text-red-400 mt-2">{flyerError}</p>}
              </div>
            </div>
          </div>
          {FIELDS.map(({ key, label }) => (
            <div key={key}>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{label} ({items[key].length})</h2>
              {items[key].length === 0 ? (
                <p className="text-sm text-gray-600">No items.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {items[key].map((item, idx) => (
                    <GalleryThumb
                      key={item.key}
                      item={item}
                      busy={busyKeys.has(item.key)}
                      onApprove={() => handleApprove(key, item)}
                      onReject={(reason) => handleReject(key, item, reason)}
                      onDelete={() => handleDeleteGalleryItem(key, item)}
                      onView={() => setSelectedItem(item)}
                      dealerId={dealSummary?.dealer_id}
                      dealerName={dealSummary?.dealer_name}
                      downloadName={`${slugifyTitle(dealSummary?.title)}-${key}-${idx + 1}.${extFromUrl(item.display_url)}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedItem && (
        <MediaLightbox
          url={selectedItem.display_url!}
          isVideo={selectedItem.media_type === "video"}
          alt={selectedItem.media_type}
          onClose={() => setSelectedItem(null)}
        />
      )}
      {lightboxUrl && (
        <MediaLightbox url={lightboxUrl.url} alt={lightboxUrl.alt} onClose={() => setLightboxUrl(null)} />
      )}

      {pendingFlyerFile && (
        <ConfirmFlyerReplaceModal
          file={pendingFlyerFile}
          currentAiFlyerUrl={aiFlyerUrl}
          busy={flyerBusy}
          onCancel={() => setPendingFlyerFile(null)}
          onConfirm={confirmFlyerReplace}
        />
      )}

      {pendingFlyerOverrideFile && (
        <ConfirmFlyerOverrideModal
          file={pendingFlyerOverrideFile}
          currentAiFlyerUrl={aiFlyerUrl}
          busy={flyerOverrideBusy}
          onCancel={() => setPendingFlyerOverrideFile(null)}
          onConfirm={confirmFlyerOverride}
        />
      )}

      {confirmingDeleteOriginal && (
        <ConfirmDeleteModal
          title={`Delete original ${confirmingDeleteOriginal}?`}
          description={`This permanently removes the deal's original ${confirmingDeleteOriginal} — it can't be undone. Any other original field (and the AI-generated flyer) is left as-is.`}
          locations={["S3 storage (the file itself)", "This deal's document (deals collection)", "This deal's typed collection (e.g. deal_stays), if mirrored there"]}
          busy={flyerBusy}
          confirmLabel={`Delete ${confirmingDeleteOriginal}`}
          onCancel={() => setConfirmingDeleteOriginal(null)}
          onConfirm={() => handleDeleteOriginal(confirmingDeleteOriginal)}
        />
      )}

      {confirmingDeleteFlyer && (
        <ConfirmDeleteModal
          title="Delete AI-generated flyer?"
          description="This permanently removes the AI-generated flyer — it can't be undone. The original asset is left as-is."
          locations={["S3 storage (the file itself)", "This deal's document (deals collection)", "This deal's typed collection (e.g. deal_stays), if mirrored there"]}
          busy={flyerDeleteBusy}
          confirmLabel="Delete AI flyer"
          onCancel={() => setConfirmingDeleteFlyer(false)}
          onConfirm={handleDeleteAiFlyer}
        />
      )}
    </div>
  );
};

export default AdminGalleryReviewPage;
